import React, { useEffect, useRef } from "react";
import QRCodeStyling from "qr-code-styling";
import useToast from "../hooks/useToast";

// ─── Icons ─────────────────────────────────────────────────────────────────────
const IconCopy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// ─── Theme ─────────────────────────────────────────────────────────────────────
function getTheme(isDark) {
  return isDark
    ? {
        bg: "#020617",
        card: "#111827",
        cardInner: "#0d1424",
        border: "rgba(99,102,241,0.12)",
        borderStrong: "rgba(99,102,241,0.28)",
        fg: "#f1f5f9",
        fgMuted: "#94a3b8",
        fgSubtle: "#475569",
        primary: "#6366f1",
        accent: "#8b5cf6",
        success: "#10b981",
        qrBg: "#ffffff",
        divider: "rgba(99,102,241,0.1)",
      }
    : {
        bg: "#f8fafc",
        card: "#ffffff",
        cardInner: "#f8fafc",
        border: "rgba(99,102,241,0.13)",
        borderStrong: "rgba(99,102,241,0.3)",
        fg: "#0f172a",
        fgMuted: "#475569",
        fgSubtle: "#94a3b8",
        primary: "#6366f1",
        accent: "#8b5cf6",
        success: "#059669",
        qrBg: "#ffffff",
        divider: "rgba(99,102,241,0.08)",
      };
}

// ─── Action button ─────────────────────────────────────────────────────────────
function ActionBtn({ icon, label, onClick, variant = "default", t, compact = false }) {
  const isGradient = variant === "gradient";
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        padding: compact ? "10px 16px" : "13px 20px",
        border: isGradient ? "none" : `1.5px solid ${t.borderStrong}`,
        borderRadius: compact ? 10 : 12,
        background: isGradient
          ? `linear-gradient(135deg, ${t.primary}, ${t.accent})`
          : "transparent",
        color: isGradient ? "#fff" : t.primary,
        fontFamily: "'DM Sans', 'Inter', sans-serif",
        fontSize: compact ? "0.82rem" : "0.88rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
        cursor: "pointer",
        transition: "opacity 0.18s, transform 0.18s, box-shadow 0.18s",
        boxShadow: isGradient ? `0 4px 20px rgba(99,102,241,0.28)` : "none",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = "0.85";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "1";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "translateY(1px)";
        e.currentTarget.style.opacity = "0.75";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.opacity = "0.85";
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Stat badge ────────────────────────────────────────────────────────────────
function StatBadge({ label, value, t }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        flex: 1,
        padding: "10px 8px",
        background: t.cardInner,
        border: `1px solid ${t.divider}`,
        borderRadius: 10,
      }}
    >
      <span
        style={{
          fontSize: "1.15rem",
          fontWeight: 700,
          color: t.primary,
          fontFamily: "'DM Sans', 'Inter', sans-serif",
          lineHeight: 1,
        }}
      >
        {value ?? 0}
      </span>
      <span
        style={{
          fontSize: "0.62rem",
          color: t.fgSubtle,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          textAlign: "center",
          fontFamily: "'DM Sans', 'Inter', sans-serif",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────
/**
 * QrCode — персональный реферальный QR-код участника.
 *
 * Props:
 *   url     — реферальный URL (https://app.com/events/slug?ref=abc123xyz)
 *             или обычный URL ивента если реферал ещё не загружен
 *   stats   — объект { scan_count, unique_scan_count, registration_count }
 *             если передан — показывает блок статистики под QR (опционально)
 *   size    — размер QR в px (default: 320)
 *   lang    — 'en' | 'fr'
 *   theme   — 'dark' | 'light'
 *   compact — компактный режим для мобильного (уменьшает отступы и шрифты)
 */
export default function QrCode({ url, stats, size = 320, lang = "en", theme = "dark", compact = false }) {
  const ref = useRef(null);
  const qrInstanceRef = useRef(null);
  const toast = useToast();
  const isDark = theme === "dark";
  const t = getTheme(isDark);

  // QR render — pixel-perfect на любом экране через ResizeObserver
  useEffect(() => {
    if (!url || !ref.current) return;

    // Минимальный размер для скачивания (всегда не меньше 1200px в буфере)
    const DOWNLOAD_MIN_PX = 1200;

    function renderQr(containerPx) {
      if (!ref.current) return;

      // dpr: берём реальный, минимум 2 — гарантирует чёткость на Retina
      const dpr = Math.max(
        typeof window !== "undefined" ? (window.devicePixelRatio || 1) : 1,
        2
      );

      // Рендерим в размер, достаточный и для экрана, и для скачивания PNG
      const renderSize = Math.max(containerPx * dpr, DOWNLOAD_MIN_PX);

      const qr = new QRCodeStyling({
        type: "canvas",
        width: renderSize,
        height: renderSize,
        data: url,
        margin: Math.round(renderSize * 0.038), // ~3.8% поля, адаптивно
        qrOptions: { errorCorrectionLevel: "H" },
        dotsOptions: {
          type: "rounded",
          color: "#6366f1",
        },
        cornersSquareOptions: {
          type: "extra-rounded",
          color: "#4f46e5",
        },
        cornersDotOptions: {
          type: "dot",
          color: "#8b5cf6",
        },
        backgroundOptions: {
          color: "#ffffff",
        },
      });

      ref.current.innerHTML = "";
      qr.append(ref.current);
      qrInstanceRef.current = qr;
    }

    // Первый рендер по текущей ширине контейнера (или fallback на size)
    const initialPx = ref.current.offsetWidth || size;
    renderQr(initialPx);

    // ResizeObserver: перерендерить если контейнер изменил размер
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      let lastWidth = initialPx;
      ro = new ResizeObserver((entries) => {
        const newWidth = Math.round(entries[0].contentRect.width);
        // Перерендеривать только при значимом изменении (>4px) — избегаем флаппинга
        if (Math.abs(newWidth - lastWidth) > 4 && newWidth > 0) {
          lastWidth = newWidth;
          renderQr(newWidth);
        }
      });
      ro.observe(ref.current);
    }

    return () => {
      ro?.disconnect();
      if (ref.current) ref.current.innerHTML = "";
      qrInstanceRef.current = null;
    };
  }, [url]); // size намеренно исключён — ResizeObserver сам отслеживает реальный размер

  // Handlers
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(lang === "fr" ? "Lien copié !" : "Link copied!", "success");
    } catch {
      toast("Failed to copy link", "error");
    }
  };

  const downloadQR = () => {
    try {
      const canvas = ref.current?.querySelector("canvas");
      if (!canvas) throw new Error("Canvas not found");

      let hostname = "qr-code";
      try { hostname = new URL(url).hostname.replace(/[^a-z0-9.-]/gi, "_"); } catch {}

      const a = document.createElement("a");
      a.download = `qr-${hostname}.png`;
      a.href = canvas.toDataURL("image/png", 1.0);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast(lang === "fr" ? "QR téléchargé !" : "QR downloaded!", "success");
    } catch {
      toast("Failed to download QR code", "error");
    }
  };

  // Показываем персональный заголовок если переданы stats (значит это реферальный QR)
  const isReferral = !!stats;

  return (
    <div
      style={{
        background: t.card,
        border: `1px solid ${t.border}`,
        borderRadius: compact ? 16 : 20,
        padding: compact ? 16 : 24,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Label */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: compact ? 14 : 20,
          width: "100%",
        }}
      >
        <span
          style={{
            display: "block",
            width: 3,
            height: 16,
            borderRadius: 99,
            background: `linear-gradient(180deg, ${t.primary}, ${t.accent})`,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: compact ? "0.65rem" : "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: t.fgMuted,
            fontFamily: "'DM Sans', 'Inter', sans-serif",
          }}
        >
          {isReferral
            ? (lang === "fr" ? "Mon QR d'invitation" : "My Invite QR Code")
            : (lang === "fr" ? "Code d'invitation" : "Invite QR Code")}
        </span>
      </div>

      {/* QR canvas area */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          padding: compact ? 6 : 10,
          marginBottom: compact ? 10 : 16,
          border: `1px solid ${t.border}`,
          boxShadow: isDark
            ? "0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.08)"
            : "0 4px 20px rgba(99,102,241,0.1)",
          lineHeight: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={ref}
          id="qr-canvas-wrap"
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            borderRadius: 8,
            overflow: "hidden",
          }}
        />
        <style>{`#qr-canvas-wrap canvas { width: 100% !important; height: auto !important; display: block; }`}</style>
      </div>

      {/* URL preview — hidden on mobile/compact */}
      {!compact && <div
        style={{
          width: "100%",
          padding: compact ? "8px 12px" : "10px 14px",
          background: t.cardInner,
          border: `1px solid ${t.divider}`,
          borderRadius: 10,
          marginBottom: compact ? 10 : 16,
          display: "flex",
          alignItems: "center",
          gap: 8,
          overflow: "hidden",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.fgSubtle} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span
          style={{
            fontSize: compact ? "0.68rem" : "0.75rem",
            color: t.fgSubtle,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "'DM Mono', 'Fira Code', monospace",
            flex: 1,
          }}
        >
          {url}
        </span>
      </div>}

      {/* ── Referral stats block (только если переданы stats) — hidden on mobile/compact ── */}
      {isReferral && !compact && (
        <>
          {/* Stats row */}
          <div
            style={{
              width: "100%",
              display: "flex",
              gap: 8,
              marginBottom: compact ? 10 : 16,
            }}
          >
            <StatBadge
              label={lang === "fr" ? "Scans" : "Scans"}
              value={stats.scan_count}
              t={t}
            />
            <StatBadge
              label={lang === "fr" ? "Uniques" : "Unique"}
              value={stats.unique_scan_count}
              t={t}
            />
            <StatBadge
              label={lang === "fr" ? "Inscrits" : "Joined"}
              value={stats.registration_count}
              t={t}
            />
          </div>

          {/* Hint text */}
          <p
            style={{
              width: "100%",
              fontSize: compact ? "0.68rem" : "0.72rem",
              color: t.fgSubtle,
              textAlign: "center",
              lineHeight: 1.6,
              marginBottom: compact ? 10 : 16,
              fontFamily: "'DM Sans', 'Inter', sans-serif",
            }}
          >
            {lang === "fr"
              ? "Partagez ce QR pour inviter vos amis. Vous verrez combien ont rejoint via votre lien."
              : "Share this QR to invite friends. You'll see how many joined via your link."}
          </p>
        </>
      )}

      {/* Divider */}
      <div
        style={{
          width: "100%",
          height: 1,
          background: t.divider,
          marginBottom: compact ? 10 : 16,
        }}
      />

      {/* Actions */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
        }}
      >
        <ActionBtn
          icon={<IconDownload />}
          label={lang === "fr" ? "Télécharger le QR" : "Download QR"}
          onClick={downloadQR}
          variant="gradient"
          t={t}
          compact={compact}
        />
        <ActionBtn
          icon={<IconCopy />}
          label={lang === "fr" ? "Copier le lien" : "Copy link"}
          onClick={copyLink}
          variant="default"
          t={t}
          compact={compact}
        />
      </div>
    </div>
  );
}